import { useState, useEffect } from "react";
import { Check } from "lucide-react";
import { Reveal, Label } from "./Motion";
import ChapterMark from "./ChapterMark";
import { PlanRequestModal } from "./PlanRequestModal";
import { api } from "../../lib/api";

export default function Pricing() {
  const [packages, setPackages] = useState([]);
  // Package the visitor is requesting — opens the request form when set.
  const [pendingPkg, setPendingPkg] = useState(null);

  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
  }, []);

  const requestPlan = (pkg) => setPendingPkg(pkg);

  return (
    <section id="pricing" data-testid="pricing-section" className="relative py-28 lg:py-40 bg-[#ded9cb] border-y border-black/10">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <ChapterMark n="05" title="Blueprint" className="mb-5" />
          <Label>Elevare Business Improvement Blueprint</Label>
          <h2 className="mt-6 font-head text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-[#1e3d3a]">
            Elevare&apos;s Three Different Levels of Engagement.
          </h2>
          <p className="mt-6 text-base text-black/60 leading-relaxed">
            Every plan begins with a strategist reviewing your books within 72 hours. From that
            call, we design the recovery, execute it, and hand you back a business you actually
            want to keep.
          </p>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
          {packages.map((pkg, idx) => (
            <Reveal key={pkg.lookup_key} delay={idx * 0.08}>
              <div
                data-testid={`pricing-card-${pkg.lookup_key}`}
                className={`relative h-full rounded-2xl border border-[#1e3d3a] p-8 flex flex-col bg-white transition-all duration-500 hover:-translate-y-1 ${
                  pkg.popular ? "shadow-xl" : ""
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-3 left-6 rounded-full bg-[#c9a961] px-3 py-1 text-[10px] uppercase tracking-widest text-[#1e3d3a] font-medium">
                    Most engaged
                  </div>
                )}
                <p className="text-sm uppercase tracking-[0.28em] text-[#a3823f] font-bold">{pkg.name}</p>
                <p className="mt-6 text-xs uppercase tracking-[0.22em] text-[#a3823f] font-bold">{pkg.tagline}</p>
                <p className="mt-3 text-sm text-black/60 leading-relaxed">{pkg.description}</p>
                <ul className="mt-8 space-y-3 mb-8 flex-1">
                  {(pkg.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-black/70">
                      <Check className="h-4 w-4 text-[#6b7c3a] flex-shrink-0 mt-0.5" strokeWidth={2.5} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <button
                  data-testid={`checkout-btn-${pkg.lookup_key}`}
                  onClick={() => requestPlan(pkg)}
                  className={`w-full py-3.5 rounded-full text-sm font-medium tracking-wide transition-all duration-500 flex items-center justify-center gap-2 ${
                    pkg.popular
                      ? "bg-[#1e3d3a] text-white hover:bg-[#c9a961] hover:text-[#1e3d3a]"
                      : "border border-[#1e3d3a] text-[#1e3d3a] hover:bg-[#1e3d3a] hover:text-white"
                  }`}
                >
                  {`Start ${pkg.name}`}
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <p className="mt-8 text-xs text-black/40 text-center">
          No payment now — tell us about your business and we'll email you a secure link to activate your plan.
        </p>
      </div>

      {/* Requesting a plan opens a short form; founders price it and email a link. */}
      <PlanRequestModal
        open={!!pendingPkg}
        pkg={pendingPkg}
        onClose={() => setPendingPkg(null)}
      />
    </section>
  );
}
